import React, { createContext, useState, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenantConfig, setTenantConfig] = useState({
    date_format: 'DD/MM/YYYY',
    currency: 'USD',
    timezone: 'UTC',
    measurement_system: 'metric',
    number_format: '1,234.56'
  });
  const [loading, setLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedConfig = localStorage.getItem('tenant_config');
    
    if (storedConfig) {
      try {
        setTenantConfig(JSON.parse(storedConfig));
      } catch (error) {
        console.error('Error parsing tenant config:', error);
      }
    }
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      // Verify token is still valid
      api.get('/auth/me')
        .then((response) => {
          const userData = response.data;
          setUser(userData);
          
          // Store redirect path
          if (userData.redirect_path) {
            setRedirectPath(userData.redirect_path);
            localStorage.setItem('redirect_path', userData.redirect_path);
          }
          
          if (userData.tenant_config) {
            setTenantConfig(userData.tenant_config);
            localStorage.setItem('tenant_config', JSON.stringify(userData.tenant_config));
          }
          
          // Store tenant subdomain if available
          if (userData.tenant_subdomain) {
            localStorage.setItem('tenant_subdomain', userData.tenant_subdomain);
          }
          
          localStorage.setItem('user', JSON.stringify(userData));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tenant_id');
          localStorage.removeItem('tenant_config');
          localStorage.removeItem('tenant_subdomain');
          localStorage.removeItem('redirect_path');
          setUser(null);
          setRedirectPath(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData, redirect_path, tenant_subdomain } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (userData.tenant_id) {
        localStorage.setItem('tenant_id', userData.tenant_id);
      }
      
      if (tenant_subdomain) {
        localStorage.setItem('tenant_subdomain', tenant_subdomain);
      }
      
      if (redirect_path) {
        setRedirectPath(redirect_path);
        localStorage.setItem('redirect_path', redirect_path);
      }
      
      setUser(userData);
      
      // Fetch full user data including tenant config
      try {
        const configResponse = await api.get('/auth/me');
        const fullUserData = configResponse.data;
        setUser(fullUserData);
        
        if (fullUserData.tenant_config) {
          setTenantConfig(fullUserData.tenant_config);
          localStorage.setItem('tenant_config', JSON.stringify(fullUserData.tenant_config));
        }
        
        if (fullUserData.redirect_path) {
          setRedirectPath(fullUserData.redirect_path);
          localStorage.setItem('redirect_path', fullUserData.redirect_path);
        }
        
        if (fullUserData.tenant_subdomain) {
          localStorage.setItem('tenant_subdomain', fullUserData.tenant_subdomain);
        }
        
        localStorage.setItem('user', JSON.stringify(fullUserData));
      } catch (error) {
        console.error('Error fetching user config:', error);
      }
      
      return { success: true, redirect_path: redirect_path || userData.redirect_path };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const register = async (data) => {
    try {
      const response = await api.post('/auth/register', data);
      const { access_token, user: userData, tenant } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('tenant_id', userData.tenant_id);
      
      setUser(userData);
      
      // Fetch tenant config
      try {
        const configResponse = await api.get('/auth/me');
        if (configResponse.data.tenant_config) {
          setTenantConfig(configResponse.data.tenant_config);
          localStorage.setItem('tenant_config', JSON.stringify(configResponse.data.tenant_config));
        }
      } catch (error) {
        console.error('Error fetching tenant config:', error);
      }
      
      return { success: true, tenant };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout API to log the activity
      await api.post('/auth/logout');
    } catch (error) {
      // Don't fail logout if API call fails
      console.error('Error logging out:', error);
    } finally {
      // Clear local storage regardless of API call result
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('tenant_config');
      localStorage.removeItem('tenant_subdomain');
      localStorage.removeItem('redirect_path');
      setUser(null);
      setRedirectPath(null);
      setTenantConfig({
        date_format: 'DD/MM/YYYY',
        currency: 'USD',
        timezone: 'UTC',
        measurement_system: 'metric'
      });
    }
  };

  const updateTenantConfig = (newConfig) => {
    setTenantConfig(newConfig);
    localStorage.setItem('tenant_config', JSON.stringify(newConfig));
  };

  return (
    <AuthContext.Provider value={{ user, tenantConfig, login, register, logout, loading, redirectPath, updateTenantConfig }}>
      {children}
    </AuthContext.Provider>
  );
};


